import React from 'react';
import { ClockIcon, UsersIcon } from '@heroicons/react/24/solid';

const SessionDetailsModal = ({
  show,
  handleClose,
  viewingSession,
  crews,
  formatDisplayDate,
  formatSessionTiming,
}) => {
  if (!show || !viewingSession) return null;

  const getCrewById = (crewId) => crews.find((c) => c.id.toString() === crewId.toString());

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-600 bg-opacity-75 p-2 md:p-4"
      onClick={handleClose}
    >
      <div
        className="max-h-[95vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white md:max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-gray-200 px-4 py-4 md:px-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Session Details: {viewingSession.title}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {viewingSession.childSessions && viewingSession.childSessions.length > 0
                  ? `Parent session with ${viewingSession.childSessions.length} scheduled time slot${viewingSession.childSessions.length > 1 ? 's' : ''}`
                  : viewingSession.parent_session_id
                    ? 'Child session'
                    : 'Individual session'}
              </p>
            </div>
            <div className="text-right">
              <span
                className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                  viewingSession.session_type === 'scheduled'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}
              >
                {viewingSession.session_type === 'scheduled' ? 'Scheduled' : 'Unscheduled'}
              </span>
            </div>
          </div>
        </div>

        <div className="p-4 md:p-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="space-y-4">
              <div>
                <h4 className="mb-3 text-sm font-medium text-gray-900">Session Information</h4>
                <div className="space-y-3 rounded-lg bg-gray-50 p-4">
                  <div>
                    <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      Description
                    </span>
                    <p className="mt-1 text-sm text-gray-900">
                      {viewingSession.description || 'No description provided'}
                    </p>
                  </div>

                  <div>
                    <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      Date
                    </span>
                    <p className="mt-1 text-sm text-gray-900">
                      {formatDisplayDate(new Date(viewingSession.date))}
                    </p>
                  </div>

                  <div>
                    <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      Time & Duration
                    </span>
                    <p className="mt-1 text-sm text-gray-900">
                      {formatSessionTiming(viewingSession)}
                    </p>
                  </div>

                  {viewingSession.location && (
                    <div>
                      <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                        Location
                      </span>
                      <p className="mt-1 text-sm text-gray-900">{viewingSession.location}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <div className="mb-3 flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-900">
                    {viewingSession.childSessions && viewingSession.childSessions.length > 0
                      ? 'Crew Assignments by Time Slot'
                      : 'Crew Assignments'}
                  </h4>
                  <div className="rounded bg-blue-50 px-2 py-1 text-xs text-gray-500">
                    💡 Session lineups are used for experimental crews.
                  </div>
                </div>

                {viewingSession.childSessions && viewingSession.childSessions.length > 0 ? (
                  <div className="space-y-3">
                    {viewingSession.childSessions.map((childSession) => (
                      <div key={childSession.id} className="rounded-lg border bg-white p-4">
                        <div className="mb-3 flex items-start justify-between">
                          <div>
                            <h5 className="text-sm font-medium text-gray-900">
                              {formatSessionTiming(childSession)}
                            </h5>
                            <p className="text-xs text-gray-500">
                              {formatDisplayDate(new Date(childSession.date))}
                            </p>
                          </div>
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                              childSession.session_type === 'scheduled'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}
                          >
                            {childSession.session_type === 'scheduled'
                              ? 'Scheduled'
                              : 'Unscheduled'}
                          </span>
                        </div>

                        <div>
                          <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                            Crews
                          </span>
                          <div className="mt-1">
                            {!childSession.crew_assignments ||
                            childSession.crew_assignments.length === 0 ? (
                              <span className="text-sm italic text-gray-500">
                                No crews assigned
                              </span>
                            ) : (
                              <div className="space-y-1">
                                {childSession.crew_assignments.map((assignment, index) => {
                                  const crew = getCrewById(assignment.crew_id);
                                  return (
                                    <div
                                      key={index}
                                      className="rounded border border-gray-200 bg-white p-2"
                                    >
                                      <div className="mb-2 flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-900">
                                          {crew?.name || 'Unknown Crew'}
                                        </span>
                                        {crew && (
                                          <div className="flex items-center gap-1">
                                            <span className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-500">
                                              {crew.boat_type}
                                            </span>
                                            {crew.is_experimental && (
                                              <span className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-600">
                                                EXP
                                              </span>
                                            )}
                                          </div>
                                        )}
                                      </div>

                                      {/* Complete Crew Lineup for this specific session time */}
                                      <div className="mt-3 border-t pt-2">
                                        <div className="mb-2 text-xs font-medium text-gray-500">
                                          Complete Seat Assignments:
                                        </div>

                                        {assignment.lineup && assignment.lineup.length > 0 ? (
                                          <div className="space-y-1">
                                            {assignment.lineup
                                              .sort(
                                                (a, b) =>
                                                  (a.seat_number || 0) - (b.seat_number || 0)
                                              )
                                              .map((lineupMember, lineupIndex) => (
                                                <div
                                                  key={lineupIndex}
                                                  className="flex items-center justify-between rounded bg-blue-50 p-2 text-xs"
                                                >
                                                  <div className="flex items-center">
                                                    <span className="mr-2 flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                                                      {lineupMember.seat_number === 0
                                                        ? 'C'
                                                        : lineupMember.seat_number}
                                                    </span>
                                                    <span className="font-medium text-blue-900">
                                                      {lineupMember.user?.first_name}{' '}
                                                      {lineupMember.user?.last_name || 'Unknown'}
                                                    </span>
                                                    <span className="ml-2 text-blue-700">
                                                      {lineupMember.seat_number === 0
                                                        ? '(Cox)'
                                                        : lineupMember.side
                                                          ? `(${lineupMember.side})`
                                                          : ''}
                                                    </span>
                                                  </div>
                                                  {lineupMember.user?.preferred_side &&
                                                    lineupMember.user.preferred_side !== 'none' && (
                                                      <span className="rounded bg-blue-200 px-2 py-0.5 text-xs text-blue-700">
                                                        {lineupMember.user.preferred_side}
                                                      </span>
                                                    )}
                                                </div>
                                              ))}
                                          </div>
                                        ) : (
                                          <div className="text-xs italic text-gray-500">
                                            No specific lineup assigned - using default crew
                                            positions
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}

                    {viewingSession.crew_assignments &&
                      viewingSession.crew_assignments.length > 0 && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                          <div className="mb-2 flex items-center">
                            <span className="text-sm font-medium text-amber-800">
                              Unscheduled Crews
                            </span>
                            <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                              {viewingSession.crew_assignments.length}
                            </span>
                          </div>
                          <div className="space-y-2">
                            {viewingSession.crew_assignments.map((assignment, index) => {
                              const crew = getCrewById(assignment.crew_id);
                              return (
                                <div
                                  key={index}
                                  className="rounded border border-amber-200 bg-amber-100 p-2"
                                >
                                  <div className="mb-2 flex items-center justify-between">
                                    <span className="text-sm font-medium text-amber-900">
                                      {crew?.name || 'Unknown Crew'}
                                    </span>
                                    {crew && (
                                      <div className="flex items-center gap-1">
                                        <span className="rounded bg-amber-200 px-2 py-1 text-xs text-amber-700">
                                          {crew.boat_type}
                                        </span>
                                        {crew.is_experimental && (
                                          <span className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-600">
                                            EXP
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>

                                  <div className="mt-3 border-t border-amber-200 pt-2">
                                    <div className="mb-2 text-xs font-medium text-amber-800">
                                      Complete Lineup:
                                    </div>

                                    {assignment.lineup && assignment.lineup.length > 0 ? (
                                      <div className="space-y-1">
                                        {assignment.lineup
                                          .sort(
                                            (a, b) => (a.seat_number || 0) - (b.seat_number || 0)
                                          )
                                          .map((lineupMember, lineupIndex) => (
                                            <div
                                              key={lineupIndex}
                                              className="flex items-center justify-between rounded bg-amber-100 p-2 text-xs"
                                            >
                                              <div className="flex items-center">
                                                <span className="mr-2 flex h-6 w-6 items-center justify-center rounded-full bg-amber-600 text-xs font-bold text-white">
                                                  {lineupMember.seat_number === 0
                                                    ? 'C'
                                                    : lineupMember.seat_number}
                                                </span>
                                                <span className="font-medium text-amber-900">
                                                  {lineupMember.user?.first_name}{' '}
                                                  {lineupMember.user?.last_name || 'Unknown'}
                                                </span>
                                                <span className="ml-2 text-amber-700">
                                                  {lineupMember.seat_number === 0
                                                    ? '(Cox)'
                                                    : lineupMember.side
                                                      ? `(${lineupMember.side})`
                                                      : '(Scull)'}
                                                </span>
                                              </div>
                                              {lineupMember.user?.preferred_side &&
                                                lineupMember.user.preferred_side !== 'none' && (
                                                  <span className="rounded bg-amber-200 px-2 py-0.5 text-xs text-amber-800">
                                                    {lineupMember.user.preferred_side}
                                                  </span>
                                                )}
                                            </div>
                                          ))}
                                      </div>
                                    ) : crew?.members && crew.members.length > 0 ? (
                                      <div className="space-y-1">
                                        <div className="mb-1 text-xs text-amber-700">
                                          Default crew positions (all {crew.members.length}{' '}
                                          members):
                                        </div>
                                        {crew.members
                                          .sort(
                                            (a, b) => (a.seat_number || 0) - (b.seat_number || 0)
                                          )
                                          .map((member, memberIndex) => (
                                            <div
                                              key={memberIndex}
                                              className="flex items-center justify-between rounded border border-amber-200 bg-amber-50 p-2 text-xs"
                                            >
                                              <div className="flex items-center">
                                                <span className="mr-2 flex h-6 w-6 items-center justify-center rounded-full bg-amber-400 text-xs font-medium text-white">
                                                  {member.seat_number === 0
                                                    ? 'C'
                                                    : member.seat_number || '?'}
                                                </span>
                                                <span className="text-amber-900">
                                                  {member.user?.first_name}{' '}
                                                  {member.user?.last_name || 'Unknown'}
                                                </span>
                                              </div>
                                              {member.user?.preferred_side &&
                                                member.user.preferred_side !== 'none' && (
                                                  <span className="rounded bg-amber-200 px-2 py-0.5 text-xs text-amber-700">
                                                    {member.user.preferred_side}
                                                  </span>
                                                )}
                                            </div>
                                          ))}
                                      </div>
                                    ) : (
                                      <div className="text-xs italic text-amber-700">
                                        No lineup information available
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <p className="mt-2 text-xs text-amber-700">
                            These crews can be scheduled to specific times using the Planner.
                          </p>
                        </div>
                      )}
                  </div>
                ) : (
                  <div className="rounded-lg bg-gray-50 p-4">
                    {!viewingSession.crew_assignments ||
                    viewingSession.crew_assignments.length === 0 ? (
                      <div className="py-4 text-center">
                        <span className="text-sm italic text-gray-500">
                          {viewingSession.childSessions && viewingSession.childSessions.length > 0
                            ? 'All crews have been scheduled'
                            : 'No crews assigned - Open session'}
                        </span>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {viewingSession.crew_assignments.map((assignment, index) => {
                          const crew = getCrewById(assignment.crew_id);
                          return (
                            <div key={index} className="rounded border bg-gray-100 p-3">
                              <div className="mb-2 flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-900">
                                  {crew?.name || 'Unknown Crew'}
                                </span>
                                {crew && (
                                  <div className="flex items-center gap-1">
                                    <span className="rounded bg-gray-200 px-2 py-1 text-xs text-gray-500">
                                      {crew.boat_type}
                                    </span>
                                    {crew.is_experimental && (
                                      <span className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-600">
                                        EXP
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* Show lineup if available, otherwise show default crew positions */}
                              <div className="mt-2 border-t pt-2">
                                <div className="mb-2 text-xs font-medium text-gray-500">
                                  {assignment.lineup && assignment.lineup.length > 0
                                    ? 'Session Lineup:'
                                    : 'Default Crew Positions:'}
                                </div>

                                {assignment.lineup && assignment.lineup.length > 0 ? (
                                  <div className="space-y-1">
                                    {assignment.lineup
                                      .sort((a, b) => (a.seat_number || 0) - (b.seat_number || 0))
                                      .map((lineupMember, lineupIndex) => (
                                        <div
                                          key={lineupIndex}
                                          className="flex items-center justify-between rounded bg-green-50 p-2 text-xs"
                                        >
                                          <div className="flex items-center">
                                            <span className="mr-2 flex h-6 w-6 items-center justify-center rounded-full bg-green-600 text-xs font-bold text-white">
                                              {lineupMember.seat_number === 0
                                                ? 'C'
                                                : lineupMember.seat_number}
                                            </span>
                                            <span className="font-medium text-green-900">
                                              {lineupMember.user?.first_name}{' '}
                                              {lineupMember.user?.last_name || 'Unknown'}
                                            </span>
                                            <span className="ml-2 text-green-700">
                                              {lineupMember.seat_number === 0
                                                ? '(Cox)'
                                                : lineupMember.side
                                                  ? `(${lineupMember.side})`
                                                  : '(Scull)'}
                                            </span>
                                          </div>
                                          {lineupMember.user?.preferred_side &&
                                            lineupMember.user.preferred_side !== 'none' && (
                                              <span className="rounded bg-green-200 px-2 py-0.5 text-xs text-green-700">
                                                {lineupMember.user.preferred_side}
                                              </span>
                                            )}
                                        </div>
                                      ))}
                                  </div>
                                ) : crew?.members && crew.members.length > 0 ? (
                                  <div className="space-y-1">
                                    {crew.members
                                      .sort((a, b) => (a.seat_number || 0) - (b.seat_number || 0))
                                      .map((member, memberIndex) => (
                                        <div
                                          key={memberIndex}
                                          className="flex items-center justify-between rounded bg-gray-50 p-2 text-xs"
                                        >
                                          <div className="flex items-center">
                                            <span className="mr-2 flex h-6 w-6 items-center justify-center rounded-full bg-gray-400 text-xs font-medium text-white">
                                              {member.seat_number === 0
                                                ? 'C'
                                                : member.seat_number || '?'}
                                            </span>
                                            <span className="text-gray-900">
                                              {member.user?.first_name}{' '}
                                              {member.user?.last_name || 'Unknown'}
                                            </span>
                                          </div>
                                          {member.user?.preferred_side &&
                                            member.user.preferred_side !== 'none' && (
                                              <span className="rounded bg-gray-200 px-2 py-0.5 text-xs text-gray-700">
                                                {member.user.preferred_side}
                                              </span>
                                            )}
                                        </div>
                                      ))}
                                  </div>
                                ) : (
                                  <div className="text-xs italic text-gray-500">
                                    No lineup information available
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {viewingSession.user_assignments && viewingSession.user_assignments.length > 0 && (
                <div>
                  <h4 className="mb-3 text-sm font-medium text-gray-900">Assigned Coaches</h4>
                  <div className="rounded-lg bg-gray-50 p-4">
                    <div className="space-y-2">
                      {viewingSession.user_assignments
                        .filter((assignment) => assignment.role === 'coach')
                        .map((assignment, index) => (
                          <div key={index} className="flex items-center">
                            <div className="mr-3 flex h-8 w-8 items-center justify-center rounded-full bg-blue-100">
                              <span className="text-xs font-medium text-blue-600">
                                {assignment.user?.first_name?.[0]}
                                {assignment.user?.last_name?.[0]}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {assignment.user?.first_name} {assignment.user?.last_name}
                              </p>
                              <p className="text-xs text-gray-500">Coach</p>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button onClick={handleClose} className="btn-secondary">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionDetailsModal;
